import { settingFieldType } from 'config/settingFieldType';
import { mergedSettings } from 'library/helpers/settingsHelper';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { settingsActions } from './slice';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { Notification } from 'zComponents/atoms';

const selectedProfile = (state) => state.Auth.selectedProfile;

export function* loadOrgSettings() {
	const superFetchModule = yield import('library/helpers/superFetch');
	const superFetch = superFetchModule.default;
	try {
		const { orgID } = yield select(selectedProfile);

		if (!orgID) {
			throw new Error('No orgID found in the current profile');
		}
		const orgSettings = yield superFetch.get(`/settings`, {}, {}, orgID).then((res) => {
			if (res.statusCode === 200) {
				return res.data;
			} else {
				throw new Error('Error: ' + res.error.message);
			}
		});

		yield put(settingsActions.loadOrgSettingsSuccess({ settings: orgSettings, orgID: orgID }));
	} catch (error) {
		yield put(settingsActions.loadOrgSettingsError(error.message));
	}
}

export function* loadOrgSettingsSuccess({ payload }) {
	// merge the default settings with the org settings
	const { settings, orgID } = payload;

	const defaultSettings = yield select((state) => state.DefaultSettings.orgDefaultSettings);
	const res = mergedSettings(defaultSettings, settings);

	yield put(settingsActions.mergeOrgSettings({ orgID, mergedSettings: res }));
}

export function* loadPersonSettings() {
	const superFetchModule = yield import('library/helpers/superFetch');
	const superFetch = superFetchModule.default;
	try {
		const { orgID, personID } = yield select(selectedProfile);

		if (!orgID || !personID) {
			throw new Error('No orgID or personID found in the current profile');
		}
		const personSettings = yield superFetch.get(`/settings/people`, {}, { personID }, orgID).then((res) => {
			if (res.statusCode === 200) {
				return res.data;
			} else {
				throw new Error('Error: ' + res.error.message);
			}
		});

		yield put(settingsActions.loadPersonSettingsSuccess({ settings: personSettings, personID: personID }));
	} catch (error) {
		yield put(settingsActions.loadPersonSettingsError(error.message));
	}
}

export function* loadPersonSettingsSuccess({ payload }) {
	// merge the default settings with the person settings
	const { settings, personID } = payload;

	const defaultSettings = yield select((state) => state.DefaultSettings.personDefaultSettings);
	const res = mergedSettings(defaultSettings, settings);

	yield put(settingsActions.mergePersonSettings({ personID, mergedSettings: res }));
}

export function* updatePersonSetting({ payload }) {
	try {
		const superFetchModule = yield import('library/helpers/superFetch');
		const superFetch = superFetchModule.default;
		const { personID } = yield select(selectedProfile);

		const body = {
			defaultSettingID: payload.defaultSettingID,
			value: [settingFieldType.MULTISELECT, settingFieldType.MULTIVALUE].includes(payload.fieldTypeID)
				? JSON.stringify(payload.value)
				: payload.value,
			personID: personID
		};

		const res = yield superFetch.post('/settings/people', body);

		if (res.statusCode === 200) {
			// Reload the person settings
			yield put(settingsActions.loadPersonSettings());
		} else {
			throw new Error('Error: ' + res.error.message);
		}
	} catch (error) {
		yield put(settingsActions.updatePersonSettingError(error.message));
	}
}

/**
 * Encodes a setting's value for the API. MULTIVALUE and CREDENTIAL fields
 * are stored as JSON; everything else is stored raw. Mirrors the encoding
 * in the original handleSaveChanges so we don't introduce drift.
 */
const encodeValue = (setting) => {
	const jsonEncoded = [settingFieldType.MULTIVALUE, settingFieldType.CREDENTIAL].includes(setting.fieldTypeID);
	return jsonEncoded ? JSON.stringify(setting.value) : setting.value;
};

function* upsertOrgSettings(action) {
	const superFetchModule = yield import('library/helpers/superFetch');
	const superFetch = superFetchModule.default;

	const { settings } = action.payload || {};

	if (!Array.isArray(settings) || settings.length === 0) {
		yield put(settingsActions.upsertOrgSettingsSuccess());
		return;
	}

	try {
		yield all(
			settings.map((setting) =>
				call(
					superFetch.post,
					'/settings',
					{
						defaultSettingID: setting.defaultSettingID,
						value: encodeValue(setting)
					},
					{}
				)
			)
		);

		yield put(settingsActions.upsertOrgSettingsSuccess());
		yield put(settingsActions.loadOrgSettings());

		Notification('success', 'Settings were saved successfully.');
	} catch (error) {
		console.error('upsertOrgSettings failed:', error);
		yield put(settingsActions.upsertOrgSettingsError(error?.message || 'Failed to save settings'));
		Notification('error', 'Failed to save settings. Please try again.');
	}
}

function* upsertPersonSettings(action) {
	const superFetchModule = yield import('library/helpers/superFetch');
	const superFetch = superFetchModule.default;

	const { settings, personID } = action.payload || {};

	if (!personID) {
		yield put(settingsActions.upsertPersonSettingsError('personID is required'));
		return;
	}

	if (!Array.isArray(settings) || settings.length === 0) {
		yield put(settingsActions.upsertPersonSettingsSuccess());
		return;
	}

	try {
		yield all(
			settings.map((setting) =>
				call(
					superFetch.post,
					'/settings/people',
					{},
					{
						defaultSettingID: setting.defaultSettingID,
						value: encodeValue(setting)
					}
				)
			)
		);

		yield put(settingsActions.upsertPersonSettingsSuccess());
		yield put(settingsActions.loadPersonSettings());

		Notification('success', 'Settings were saved successfully.');
	} catch (error) {
		yield put(settingsActions.upsertPersonSettingsError(error?.message || 'Failed to save settings'));
		Notification('error', 'Failed to save settings. Please try again.');
	}
}

// ── Convenience: entity-agnostic helper for callers that have `entityType` ──
//
// Some callers (RenderContentSetting.handleSaveChanges) don't know at compile
// time whether they're saving org or person settings. They pass `entityType`
// and let this helper route to the right action. Import and dispatch:
//
//   dispatch(upsertSettingsByEntityType({ entityType, settings, personID,  }))

export const upsertSettingsByEntityType = ({ entityType, settings, personID }) => {
	if (entityType === CONSTANTS_SETTINGS.ORG_SETTING_TYPE) {
		return settingsActions.upsertOrgSettings({ settings });
	}
	return settingsActions.upsertPersonSettings({ settings, personID });
};

// The main export for the saga
export default function* settingsSagas() {
	yield takeLatest(settingsActions.loadOrgSettings, loadOrgSettings);
	yield takeLatest(settingsActions.loadPersonSettings, loadPersonSettings);
	yield takeLatest(settingsActions.loadOrgSettingsSuccess, loadOrgSettingsSuccess);
	yield takeLatest(settingsActions.loadPersonSettingsSuccess, loadPersonSettingsSuccess);
	yield takeLatest(settingsActions.updatePersonSetting, updatePersonSetting);
	yield takeEvery(settingsActions.upsertOrgSettings, upsertOrgSettings);
	yield takeEvery(settingsActions.upsertPersonSettings, upsertPersonSettings);
}
